import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  getReportDefinitions, getReportColumnLayout, reorderReportColumns,
  runReport, downloadReportPDF, getBranches, getUsers, getCustomers, getProducts,
} from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import SelectBox from "../components/UI/SelectBox";
import SearchInput from "../components/UI/SearchInput";
import FloatingInput from "../components/UI/FloatingInput";
import ToggleSwitch from "../components/UI/ToggleSwitch";
import PageHeader from "../components/UI/PageHeader";
import Pagination from "../components/UI/Pagination";
import { notify } from "../utils/notify";
import { useTranslation } from "../context/I18nContext";

const PER_PAGE = 15;

// formatCell mirrors the backend's own GeneratePDF formatting (see
// internal/reports/pdf.go's formatCell) so the on-screen table and the
// downloaded/printed PDF never disagree about how a value looks.
function formatCell(value, type) {
  if (value === null || value === undefined || value === "") return "—";
  switch (type) {
    case "money":
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    case "date":
      return new Date(value).toLocaleDateString();
    case "datetime":
      return new Date(value).toLocaleString();
    case "number":
      return Number(value).toLocaleString();
    default:
      return String(value);
  }
}

function userLabel(u) {
  const name = `${u.first_name || ""} ${u.last_name || ""}`.trim();
  return name || u.username;
}

// A report's Name/Description/Column labels/Filter labels come from the
// backend's report registry (internal/reports/registry.go — see
// Definition's own doc comment), not this page's own static strings, so
// they can't be wrapped in a plain t("key", fallback) the usual way —
// there's no key to write by hand for a report nobody's added yet. Instead
// every place below that renders one of those four fields builds its own
// dynamic key — t(`report_${key}_name`, name), `_desc`, `_col_${col.key}`,
// `_filter_${f.key}` — so a NEW report shows up correctly (in English)
// the moment it's registered on the backend, with zero changes needed
// here; making its text actually translatable just means adding the
// matching keys to seed_translations.go's "reports" block (see registry.go).

// ColumnCustomizeModal is the "report_customize"-gated screen — toggle a
// column's visibility and move it up/down within one report's fixed
// catalog. Saved layout is GLOBAL (shared by everyone who runs this report,
// not per-user — see ReportColumnLayout's own doc comment), which is why
// this is its own separate permission from just running/printing a report.
function ColumnCustomizeModal({ token, reportKey, onClose, onSaved }) {
  const { t } = useTranslation("reports");
  const { t: tCommon } = useTranslation("common");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getReportColumnLayout(token, reportKey)
      .then((rows) => setItems(rows || []))
      .catch(() => notify.error(t("notify_load_layout_failed", "Failed to load column layout")))
      .finally(() => setLoading(false));
  }, [token, reportKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const move = (index, delta) => {
    setItems((prev) => {
      const next = [...prev];
      const target = index + delta;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const toggleVisible = (index) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, visible: !it.visible } : it)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = items.map((it, i) => ({ column_key: it.column_key, visible: it.visible, sort_order: i }));
      await reorderReportColumns(token, reportKey, payload);
      notify.success(t("notify_layout_saved", "Layout saved"));
      onSaved();
    } catch (err) {
      notify.error(err.message || t("notify_save_layout_failed", "Failed to save layout"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="bg-white rounded-[2rem] p-6 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800">{t("customize_modal_title", "Customize Columns")}</h3>
            <p className="text-xs text-gray-400">{t("customize_modal_subtitle", "Show/hide and reorder — applies for everyone who runs this report")}</p>
          </div>
          <button onClick={onClose} className="text-gray-300 hover:text-red-500">
            <ICONS.Close className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-400 text-center py-6">{tCommon("loading_text", "Loading…")}</p>
        ) : (
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={it.column_key} className="flex items-center justify-between bg-gray-50/60 rounded-xl px-3 py-2">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col leading-none">
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs">▲</button>
                    <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-gray-400 hover:text-gray-700 disabled:opacity-30 text-xs">▼</button>
                  </div>
                  <span className={`text-sm font-medium ${it.visible ? "text-gray-800" : "text-gray-400 line-through"}`}>
                    {t(`report_${reportKey}_col_${it.column_key}`, it.label)}
                  </span>
                </div>
                <ToggleSwitch checked={it.visible} onChange={() => toggleVisible(i)} name={`col-${it.column_key}`} />
              </div>
            ))}
          </div>
        )}

        <div className="flex gap-3 mt-6">
          <Button variant="secondary" onClick={onClose} className="flex-1">{tCommon("btn_cancel", "Cancel")}</Button>
          <Button variant="primary" onClick={save} loading={saving} className="flex-1">{tCommon("btn_save", "Save")}</Button>
        </div>
      </div>
    </div>
  );
}

export default function Reports() {
  const { t } = useTranslation("reports");
  const navigate = useNavigate();
  const { token, userPermissions } = useContext(AuthContext);
  const canCustomize = userPermissions?.includes("report_customize");

  const [definitions, setDefinitions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKey, setSelectedKey] = useState(null);

  const [branches, setBranches] = useState([]);
  const [cashiers, setCashiers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);

  const [filterValues, setFilterValues] = useState({});
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [customizing, setCustomizing] = useState(false);

  useEffect(() => {
    getReportDefinitions(token).then(setDefinitions).catch(() => notify.error(t("notify_load_reports_failed", "Failed to load reports")));
    // Branch/cashier/customer pickers each need their own view permission
    // (branch_view/user_view/customer_view) — someone who can view this
    // report (report_view_<key>) but lacks one of those just gets that one
    // filter limited to "All …" instead of the page failing outright, so
    // these are deliberately silent.
    getBranches(token).then(setBranches).catch(() => {});
    // Unlike getBranches/getCustomers (plain arrays), getUsers returns a
    // paginated { data, meta } envelope — request a high limit so the
    // cashier picker actually gets everyone, not just the default page
    // size, and unwrap defensively the same way AddEditBranch.js does.
    getUsers(token, { limit: 1000 }).then((res) => setCashiers(res?.data || res || [])).catch(() => {});
    getCustomers(token).then(setCustomers).catch(() => {});
    getProducts(token).then(setProducts).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedDef = useMemo(() => definitions.find((d) => d.key === selectedKey) || null, [definitions, selectedKey]);

  const filteredDefinitions = useMemo(() => {
    const term = searchTerm.toLowerCase();
    if (!term) return definitions;
    return definitions.filter(
      (d) => d.name.toLowerCase().includes(term) || d.description?.toLowerCase().includes(term)
    );
  }, [definitions, searchTerm]);

  const selectReport = (key) => {
    setSelectedKey(key);
    setFilterValues({});
    setResult(null);
    setCurrentPage(1);
  };

  const setFilter = (key, value) => setFilterValues((prev) => ({ ...prev, [key]: value }));

  const buildParams = () => {
    const params = {};
    if (filterValues.date_from) params.date_from = filterValues.date_from;
    if (filterValues.date_to) params.date_to = filterValues.date_to;
    if (filterValues.branch) params.branch_id = filterValues.branch;
    if (filterValues.cashier) params.cashier_id = filterValues.cashier;
    if (filterValues.customer) params.customer_id = filterValues.customer;
    if (filterValues.product) params.product_id = filterValues.product;
    if (filterValues.period) params.period = filterValues.period;
    return params;
  };

  const runSelectedReport = async () => {
    if (!selectedDef) return;
    setRunning(true);
    try {
      const data = await runReport(token, selectedDef.key, buildParams());
      setResult(data);
      setCurrentPage(1);
    } catch (err) {
      notify.error(err.message || t("notify_run_report_failed", "Failed to run report"));
    } finally {
      setRunning(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedDef) return;
    setPdfLoading(true);
    try {
      const blob = await downloadReportPDF(token, selectedDef.key, buildParams());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${selectedDef.key}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      notify.error(err.message || t("notify_generate_pdf_failed", "Failed to generate PDF"));
    } finally {
      setPdfLoading(false);
    }
  };

  // Print reuses the exact same server-rendered PDF rather than a separate
  // window.print() of the on-screen table — opening it as a blob URL hands
  // the job to the browser's own native PDF viewer, print controls
  // included, so what gets printed is guaranteed to be the same paginated,
  // bordered table a Download would have produced.
  const handlePrint = async () => {
    if (!selectedDef) return;
    setPdfLoading(true);
    try {
      const blob = await downloadReportPDF(token, selectedDef.key, buildParams());
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) notify.error(t("notify_popup_blocked", "Please allow pop-ups to print this report"));
    } catch (err) {
      notify.error(err.message || t("notify_generate_pdf_failed", "Failed to generate PDF"));
    } finally {
      setPdfLoading(false);
    }
  };

  const branchOptions = useMemo(
    () => [{ value: "", label: t("filter_all_branches", "All branches") }, ...branches.map((b) => ({ value: String(b.id), label: b.name }))],
    [branches] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const cashierOptions = useMemo(
    () => [{ value: "", label: t("filter_all_cashiers", "All cashiers") }, ...cashiers.map((u) => ({ value: String(u.id), label: userLabel(u) }))],
    [cashiers] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const customerOptions = useMemo(
    () => [{ value: "", label: t("filter_all_customers", "All customers") }, ...customers.map((c) => ({ value: String(c.id), label: c.name }))],
    [customers] // eslint-disable-line react-hooks/exhaustive-deps
  );
  const productOptions = useMemo(
    () => [{ value: "", label: t("filter_all_products", "All products") }, ...products.map((p) => ({ value: String(p.id), label: p.name }))],
    [products] // eslint-disable-line react-hooks/exhaustive-deps
  );
  // Unlike the other filters, "period" isn't backed by a fetched list — it's
  // a fixed choice of granularity the backend already whitelists on its own
  // side (see reports.validGranularities in sales_by_period_report.go), so
  // the options here are just hardcoded to match exactly.
  const periodOptions = [
    { value: "", label: t("period_day_default", "Day (default)") },
    { value: "day", label: t("period_day", "Day") },
    { value: "week", label: t("period_week", "Week") },
    { value: "month", label: t("period_month", "Month") },
    { value: "year", label: t("period_year", "Year") },
  ];
  const filterOptionsByType = {
    branch: branchOptions,
    cashier: cashierOptions,
    customer: customerOptions,
    product: productOptions,
    period: periodOptions,
  };

  const rows = result?.rows || [];
  const columns = result?.columns || [];
  const totalPages = Math.ceil(rows.length / PER_PAGE) || 1;
  const pageRows = rows.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto transition-all duration-300">
      {customizing && selectedDef && (
        <ColumnCustomizeModal
          token={token}
          reportKey={selectedDef.key}
          onClose={() => setCustomizing(false)}
          onSaved={() => {
            setCustomizing(false);
            if (result) runSelectedReport();
          }}
        />
      )}

      <PageHeader className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6 border-b border-gray-100 pb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div className="flex items-center justify-center min-w-[48px] h-[48px] text-[var(--primary-color)]">
            <ICONS.Reports size={40} className="shrink-0" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>{t("page_title", "Reports")}</h2>
            <p className="text-gray-400 text-xs md:text-sm font-medium">{t("page_subtitle", "Search or pick a report, apply filters, then view, download, or print it")}</p>
          </div>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 h-fit">
          <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder={t("search_placeholder", "Search reports…")} className="mb-4 w-full" />
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {filteredDefinitions.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6 italic">{t("no_reports_found", "No reports found")}</p>
            ) : (
              filteredDefinitions.map((d) => (
                <button
                  key={d.key}
                  onClick={() => selectReport(d.key)}
                  className={`w-full text-start rounded-xl px-3 py-3 transition-colors ${
                    selectedKey === d.key ? "bg-[var(--primary-color)] text-white" : "bg-gray-50/60 hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  <div className="font-bold text-sm">{t(`report_${d.key}_name`, d.name)}</div>
                  <div className={`text-xs mt-0.5 ${selectedKey === d.key ? "text-white/80" : "text-gray-400"}`}>{t(`report_${d.key}_desc`, d.description)}</div>
                </button>
              ))
            )}
          </div>
        </div>

        <div className="space-y-6">
          {!selectedDef ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-10 text-center text-gray-400 italic">
              {t("empty_state_pick_report", "Pick a report on the left to get started")}
            </div>
          ) : (
            <>
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {selectedDef.filters?.map((f) => {
                    const filterLabel = t(`report_${selectedDef.key}_filter_${f.key}`, f.label);
                    if (f.type === "date_from" || f.type === "date_to") {
                      return (
                        <FloatingInput
                          key={f.key}
                          label={filterLabel}
                          type="date"
                          value={filterValues[f.key] || ""}
                          onChange={(e) => setFilter(f.key, e.target.value)}
                        />
                      );
                    }
                    const options = filterOptionsByType[f.type];
                    if (!options) return null;
                    return (
                      <SelectBox
                        key={f.key}
                        label={filterLabel}
                        options={options}
                        value={filterValues[f.key] || ""}
                        onChange={(val) => setFilter(f.key, val)}
                      />
                    );
                  })}
                </div>
                <Button variant="primary" onClick={runSelectedReport} loading={running} icon={ICONS.Reports}>
                  {t("btn_run_report", "Run Report")}
                </Button>
              </div>

              {result && (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div>
                      <h3 className="font-bold text-gray-800">{t(`report_${selectedDef.key}_name`, result.name)}</h3>
                      {result.filter_summary && <p className="text-xs text-gray-400 mt-0.5">{result.filter_summary}</p>}
                      <p className="text-[10px] text-gray-300 mt-0.5">{rows.length} {t("rows_suffix", "row(s)")}</p>
                    </div>
                    <div className="flex gap-2">
                      {canCustomize && (
                        <Button variant="secondary" size="sm" icon={ICONS.Customize} onClick={() => setCustomizing(true)}>
                          {t("btn_customize", "Customize")}
                        </Button>
                      )}
                      <Button variant="secondary" size="sm" icon={ICONS.Print} onClick={handlePrint} loading={pdfLoading}>
                        {t("btn_print", "Print")}
                      </Button>
                      <Button variant="primary" size="sm" icon={ICONS.Download} onClick={handleDownloadPDF} loading={pdfLoading}>
                        {t("btn_download_pdf", "Download PDF")}
                      </Button>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-start border-collapse">
                      <thead>
                        <tr>
                          {columns.map((col) => (
                            <th
                              key={col.key}
                              className="p-3 font-semibold text-xs uppercase text-white whitespace-nowrap"
                              style={{ backgroundColor: "var(--primary-color)" }}
                            >
                              {t(`report_${selectedDef.key}_col_${col.key}`, col.label)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pageRows.length === 0 ? (
                          <tr>
                            <td colSpan={columns.length || 1} className="p-10 text-center text-gray-400 italic">
                              {t("no_data_for_filter", "No data for this filter")}
                            </td>
                          </tr>
                        ) : (
                          pageRows.map((row, i) => (
                            <tr key={i} className={i % 2 !== 0 ? "bg-gray-50/40" : "bg-white"}>
                              {columns.map((col) => (
                                <td key={col.key} className="p-3 text-sm text-gray-700 whitespace-nowrap">
                                  {formatCell(row[col.key], col.type)}
                                </td>
                              ))}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    totalItems={rows.length}
                    currentCount={pageRows.length}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
